import React from 'react';
import { toast } from 'react-toastify';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withStyles, WithStyles } from '@material-ui/core/styles';

import { getSlugsOptions } from './redux';
import { prepareOptions } from '../helpers';

const MIN_INPUT_LENGTH = 3;

const styles = {
  option: {
    '& > div': {
      display: 'flex',
      flexDirection: 'column',
    },
    '& > div > span:first-child': {
      fontWeight: 'bold',
    },
    '& > div > span:nth-child(2) ': {
      fontSize: 14,
    },
  },
};

interface IOption {
  firstLetter?: string;
  title: string;
  id: string;
  slug: string;
}

interface IProps {
  name?: string;
  value: any;
  label: string;
  onChange(value: unknown): unknown;
  type?: string;
}

interface IState {
  options: [] | IOption[];
  loading: boolean;
  open: boolean;
  value: IOption | null;
}

interface Props extends WithStyles<typeof styles> {
  children?: React.ReactNode;
  className?: string;
}

type IOwnProps = IProps & Props;
class SimpleAsyncDropdown extends React.Component<IOwnProps, IState> {
  constructor(props: IOwnProps) {
    super(props);

    this.state = {
      options: [],
      loading: false,
      open: false,
      value: null,
    };
  }

  public async componentDidMount(): Promise<void> {
    if (this.props.value) {
      await this.fetchItems(this.props.value);
    }
  }

  public componentDidUpdate(prevProps: IOwnProps): void {
    if (prevProps.value !== this.props.value && !this.props.value) {
      this.setState({
        value: null,
      });
    }
  }

  private setOpen = () => {
    this.setState({
      open: true,
      loading: false,
    });
  };

  private setClose = () => {
    this.setState({
      open: false,
      loading: false,
    });
  };

  private handleChange = (e: React.ChangeEvent<{}>, value: IOption | null) => {
    this.setState({
      value,
    });

    this.props.onChange(value ? value.slug : '');
  };

  private fetchSlugOptions = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = event.target;

    this.setOpen();
    this.fetchItems(value);
  };

  private fetchItems = async (searchTerm: string) => {
    const value = searchTerm.trim();
    if (value.length < MIN_INPUT_LENGTH) {
      return;
    }

    this.setState({
      loading: true,
    });

    let response;

    try {
      response = await getSlugsOptions(value);
    } catch (error) {
      toast.error(error.message);
      return;
    } finally {
      this.setState({
        loading: false,
      });
    }

    const { type } = this.props;
    const options = prepareOptions(type, response);

    if (options.length > 0) {
      const autocompleteValue = options.find(
        (option: any) => this.props.value === option.slug,
      );

      this.setState({
        loading: false,
        options: options,
        value:
          !this.state.open && this.props.value && autocompleteValue
            ? autocompleteValue
            : this.state.value,
      });
    }
  };

  render() {
    const { options, loading, open } = this.state;
    const { label, classes } = this.props;

    return (
      <Autocomplete
        classes={{
          option: classes.option,
        }}
        open={open}
        onChange={this.handleChange}
        value={this.state.value}
        options={loading ? [] : (options as IOption[])}
        loading={loading}
        onOpen={this.setOpen}
        onClose={this.setClose}
        groupBy={(option: any) => option.firstLetter}
        getOptionLabel={(option: any) => `${option.slug}`}
        renderOption={option => (
          <div>
            <span>{option.title}</span>
            <span>{option.slug}</span>
          </div>
        )}
        renderInput={params => (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            value={this.props.value}
            onChange={this.fetchSlugOptions}
            fullWidth
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <React.Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null}
                  {params.InputProps.endAdornment}
                </React.Fragment>
              ),
            }}
          />
        )}
      />
    );
  }
}

export default withStyles(styles)(SimpleAsyncDropdown);
